import ReferToSelect from "@/app/shared/refer-to-select";
import { FilterDrawerView } from "@/components/controlled-table/table-filter";
import ToggleColumns from "@/components/table-utils/toggle-columns";
import { Operation } from "@/Core/Client";
import { formatDate } from "@/Core/Helpers/DayjsHelpers";
import { ReferToResponseDto } from "@/Dtos/refer-to-dto";
import cn from "@/utils/class-names";
import hasSearchedParams from "@/utils/has-searched-params";
import Icons from "@Components/Icon";
import { type Table as ReactTableType } from "@tanstack/react-table";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { PiFunnel, PiTrashDuotone } from "react-icons/pi";
import {
    ActionIcon,
    Button,
    Flex,
    Select,
    SelectOption,
    Title,
    Tooltip,
} from "rizzui";
import { useDebouncedCallback } from "use-debounce";
import { useFilterWithBackend } from "../../hooks/use-filter-with-backend";
import { Config, Field } from "./crud-config";
import { default as FieldElements, operatorOptions } from "./field-element";

interface Props<R extends Record<string, any>, F extends FieldValues> {
    table: ReactTableType<R>;
    config: Config<R, F>;
    title?: string;
}

export const Filters = <R extends Record<string, any>, F extends FieldValues>({
    table,
    title = "List",
    config,
}: Props<R, F>) => {
    const { reset } = useFilterWithBackend({} as any);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    return (
        <Flex align="center" justify="between" className="mb-4">
            <Flex align="center" gap="3">
                {title && (
                    <Title
                        as="h3"
                        className="rizzui-title-h3 pe-4 text-base font-semibold sm:text-lg"
                    >
                        {title}
                    </Title>
                )}

                {showFilters && !config.filterProps?.useDrawer && (
                    <FilterElements
                        fields={config.fields.filter((a) => a.hasFilter) ?? []}
                        ignoreSorting={config.filterProps?.ignoreSorting}
                        directionClassName={
                            config.filterProps?.directionClassName
                        }
                        directionMinWidth={
                            config.filterProps?.directionMinWidth
                        }
                        sortingClassName={config.filterProps?.sortingClassName}
                        sortingMinWidth={config.filterProps?.sortingMinWidth}
                    />
                )}

                {showFilters && !config.filterProps?.useDrawer && (
                    <Button
                        size="sm"
                        onClick={() => reset()}
                        variant="outline"
                        className="h-9 bg-gray-100/80 mt-6"
                    >
                        <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" />{" "}
                        Clear
                    </Button>
                )}
            </Flex>

            {config.filterProps?.useDrawer && (
                <FilterDrawerView
                    isOpen={openDrawer}
                    drawerTitle="Filters"
                    setOpenDrawer={setOpenDrawer}
                    bottomButtons={
                        <div className="sticky bottom-0 flex items-center justify-center gap-3 pb-3 pt-5">
                            {hasSearchedParams() ? (
                                <Button
                                    size="lg"
                                    variant="outline"
                                    onClick={() => {
                                        reset();
                                        setOpenDrawer(false);
                                    }}
                                    className="flex-shrink-0 bg-gray-100/80"
                                >
                                    Reset All
                                </Button>
                            ) : null}
                            <Button
                                size="lg"
                                className="w-full"
                                onClick={() => setOpenDrawer(false)}
                            >
                                Show results
                            </Button>
                        </div>
                    }
                >
                    <FilterElements
                        fields={config.fields.filter((a) => a.hasFilter) ?? []}
                        ignoreSorting={config.filterProps?.ignoreSorting}
                        directionClassName={
                            config.filterProps?.directionClassName
                        }
                        directionMinWidth={
                            config.filterProps?.directionMinWidth
                        }
                        sortingClassName={config.filterProps?.sortingClassName}
                        sortingMinWidth={config.filterProps?.sortingMinWidth}
                    />
                </FilterDrawerView>
            )}

            <Flex
                align="center"
                justify="end"
                gap="3"
                className="w-auto shrink-0"
            >
                {(config?.fields.filter((a) => a.hasFilter) ?? []).length >
                    0 && (
                    <Button
                        size="sm"
                        variant="outline"
                        className={cn(
                            "pe-3 ps-2.5 h-9 bg-gray-100/80",
                            "relative h-[34px] shadow backdrop-blur-md dark:bg-gray-100",
                            !config.filterProps?.useDrawer &&
                                showFilters &&
                                "border-dashed border-gray-700",
                        )}
                        {...(config.filterProps?.useDrawer
                            ? {
                                  onClick: () => {
                                      setOpenDrawer(() => !openDrawer);
                                  },
                              }
                            : {
                                  onClick: () =>
                                      setShowFilters(() => !showFilters),
                              })}
                    >
                        {hasSearchedParams() &&
                        !config.filterProps?.useDrawer &&
                        !showFilters ? (
                            <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" />
                        ) : (
                            <PiFunnel
                                className="me-1.5 h-[18px] w-[18px]"
                                strokeWidth={1.7}
                            />
                        )}

                        {!config.filterProps?.useDrawer && showFilters
                            ? "Hide Filters"
                            : config.filterProps?.useDrawer
                              ? "Filter"
                              : "Show Filters"}
                    </Button>
                )}
                {config.onExcelExport && (
                    <Tooltip
                        size="sm"
                        content="Excel Export"
                        placement="top"
                        color="invert"
                    >
                        <ActionIcon
                            size="sm"
                            variant="flat"
                            // color={isExporting ? "secondary" : "primary"}
                            className={cn(
                                "relative h-[34px] w-[34px] shadow backdrop-blur-md dark:bg-gray-100 md:h-9 md:w-9",
                                // "h-9 pe-3 ps-2.5",
                                // "bg-gray-200/70 @lg:col-span-full float-right @4xl:w-auto hover:text-green-500",
                            )}
                            onClick={() => config.onExcelExport?.()}
                        >
                            <Icons
                                icon="vscode-icons:file-type-excel"
                                className="h-[18px] w-[18px]"
                            />
                        </ActionIcon>
                    </Tooltip>
                )}

                {config.extraActions
                    ?.filter((a) => a.position === "onFilterBtn")
                    ?.map((a, i) => {
                        if (a.type === "ActionIcon") {
                            return (
                                <Tooltip
                                    size="sm"
                                    content={a.title}
                                    placement="top"
                                    color="invert"
                                >
                                    <ActionIcon
                                        size={a.size ?? "sm"}
                                        variant={a.variant ?? "flat"}
                                        className={cn(
                                            "relative h-[34px] w-[34px] shadow backdrop-blur-md dark:bg-gray-100 md:h-9 md:w-9",
                                            // "h-9 pe-3 ps-2.5",
                                            // "bg-gray-200/70 @lg:col-span-full float-right @4xl:w-auto hover:text-green-500",
                                        )}
                                        onClick={() => a?.onClick?.()}
                                    >
                                        <Icons
                                            icon="vscode-icons:file-type-excel"
                                            className="h-[18px] w-[18px]"
                                        />
                                    </ActionIcon>
                                </Tooltip>
                            );
                        }

                        if (a.type === "Button")
                            return (
                                <Button
                                    key={i}
                                    size={a.size ?? "sm"}
                                    variant={a.variant ?? "outline"}
                                    className={cn("w-auto", a.className)}
                                    onClick={() => a?.onClick?.()}
                                >
                                    {a.title}
                                </Button>
                            );

                        return <></>;
                    })}

                {table && <ToggleColumns table={table} />}
            </Flex>
        </Flex>
    );
};

interface miniProps<T extends FieldValues> {
    fields: Field<T>[];
    ignoreSorting?: boolean;
    sortingClassName?: string;
    sortingMinWidth?: string;
    directionClassName?: string;
    directionMinWidth?: string;
}

const FilterElements = <T extends FieldValues>({
    fields,
    ignoreSorting = false,
    sortingClassName,
    sortingMinWidth,
    directionClassName,
    directionMinWidth,
}: miniProps<T>) => {
    const { applyFilter, getValue, getOperator } = useFilterWithBackend<
        typeof fields,
        any
    >({} as any);

    return (
        <>
            {fields.map((f, t) => {
                if (f.type === "referTo") {
                    return (
                        <ReferToSelect<ReferToResponseDto>
                            title={f.label}
                            name={f.name as string}
                            defaultValue={getValue(f.name! as string) ?? ""}
                            className={cn("col-span-full", f.filterClassName)}
                            referToEndPoint={f?.referEndPoint ?? ""}
                            onSelected={(e) =>
                                applyFilter(f.name as string, e as string)
                            }
                            {...f.props}
                        />
                    );
                }

                return (
                    <Flex
                        align="center"
                        justify="between"
                        gap="2"
                        className={cn("w-full shrink-0", f.filterClassName)}
                    >
                        <FieldElements
                            field={f}
                            minWidth={f.minWidth}
                            fieldType={f.type}
                            options={f?.options ?? []}
                            rerender={f.element}
                            label={f.label}
                            name={`${f.name! as string}filterValue`}
                            placeholder={f.name?.toString()?.capitalize()}
                            fieldValue={
                                f.type === "select"
                                    ? (f.options?.find(
                                          (a) =>
                                              a.value ===
                                              getValue(f.name! as string),
                                      ) ?? undefined)
                                    : getValue(f.name! as string)
                            }
                            onChange={useDebouncedCallback((e) => {
                                if (f.type === "select") {
                                    applyFilter(
                                        `${f.name! as string}.value`,
                                        e.value,
                                        f.filter?.operation,
                                    );
                                } else if (f.type === "date") {
                                    applyFilter(
                                        `${f.name! as string}.value`,
                                        formatDate({
                                            value: e,
                                            format: f.dateFormat,
                                        }),
                                        f.filter?.operation,
                                    );
                                } else {
                                    applyFilter(
                                        `${f.name! as string}.value`,
                                        e.target.value,
                                        f.filter?.operation,
                                    );
                                }
                            }, 500)}
                            {...f.props}
                        />

                        {f.filter?.showOperations && (
                            <Select
                                size="md"
                                label={`${f.label} Operation`}
                                className={cn(`w-full`, f.filterClassName)}
                                placeholder="Select operation"
                                key={`${f.name?.toString() || t + 2}`}
                                options={operatorOptions.filterIf(
                                    f.filter?.ignoreOperators !== undefined &&
                                        f.filter?.ignoreOperators.isNotEmpty(),
                                    (o) =>
                                        !f.filter?.ignoreOperators!.includes(
                                            o.value as unknown as Operation,
                                        ),
                                )}
                                value={operatorOptions.find(
                                    (a) =>
                                        a.value ==
                                        getOperator(f.name! as string),
                                )}
                                name={`${f.name! as string}filterOperation`}
                                // getOptionDisplayValue={(option) => option.label}
                                // getOptionValue={(option) => option.value}
                                onChange={useDebouncedCallback(
                                    (e: SelectOption) => {
                                        console.log("operation", e);

                                        applyFilter(
                                            `${f.name! as string}.operation`,
                                            e.value as string,
                                        );
                                    },
                                    500,
                                )}
                            />
                        )}
                    </Flex>
                );
            })}

            {!ignoreSorting && (
                <Flex
                    align="center"
                    justify="between"
                    gap="2"
                    className={cn(
                        "w-full shrink-0",
                        sortingClassName,
                        directionClassName,
                    )}
                >
                    <FieldElements
                        fieldType={"select"}
                        label={"Sorting"}
                        name={"SortField"}
                        minWidth={sortingMinWidth}
                        className={cn(sortingClassName)}
                        placeholder="Select Sorting"
                        value={getValue("sort")?.split(".")[0] ?? ""}
                        options={fields.map((f) => ({
                            label: f.label?.capitalize(),
                            value: f.name! as string,
                        }))}
                        onChange={useDebouncedCallback((e) => {
                            applyFilter("sort", e.value);
                        }, 500)}
                    />

                    <FieldElements
                        fieldType={"select"}
                        label="Direction"
                        name="sortDirection"
                        placeholder="Select Direction"
                        minWidth={directionMinWidth}
                        className={cn(directionClassName)}
                        value={
                            getValue("sort")
                                ? getValue("sort")?.split(".")[1] === "true"
                                    ? "Ascending"
                                    : "Descending"
                                : ""
                        }
                        options={[
                            { label: "Ascending", value: "true" },
                            { label: "Descending", value: "false" },
                        ]}
                        onChange={useDebouncedCallback((e) => {
                            applyFilter("sortDirection", e.value);
                        }, 500)}
                    />
                </Flex>
            )}
        </>
    );
};
